import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { ModalComponent } from '../../../shared/components/modal/modal';
import { SelectComponent, SelectOption } from '../../../shared/components/select/select';
import {
  DateRangePickerComponent,
  DateRange,
} from '../../../shared/components/date-range-picker/date-range-picker';
import {
  ConfiguredDataElement,
  CreateProgramPayload,
  UpdateProgramPayload,
  Program,
  ProgramDetail,
  ProgramType,
} from '../../../shared/models/program.models';
import { SituationTaxonomy, ResponseTaxonomy } from '../../../shared/models/taxonomy.models';
import { ProgramService } from '../../../core/services/program.service';
import { DataElementService } from '../../../core/services/data-element.service';
import { TaxonomyService } from '../../../core/services/taxonomy.service';
import { ToastService } from '../../../core/services/toast.service';
import { apiErrorMessage } from '../../../shared/utils/api-error';

type LocationStatus = 'Active' | 'Inactive';

interface ProgramLocation {
  locationId: string;
  name: string;
  status: LocationStatus;
  isCustom: boolean;
}
interface DataElementRow {
  id: string;
  cdeProgramId?: string;
  name: string;
  beginDate: string;
  endDate: string | null;
  levelIncidentPI: number;
  piInvolvementTypeId: string | null;
  requiredOptional: number | null;
}

@Component({
  selector: 'app-create-program-modal',
  imports: [ModalComponent, NgClass, SelectComponent, DateRangePickerComponent],
  templateUrl: './create-program-modal.html',
})
export class CreateProgramModalComponent implements OnInit, OnDestroy {
  program = input<Program | null>(null);
  closed = output<void>();
  saved = output<void>();
  removed = output<void>();

  private programService = inject(ProgramService);
  private dataElementService = inject(DataElementService);
  private taxonomyService = inject(TaxonomyService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  protected isEditMode = computed(() => !!this.program());

  private allProgramTypes = signal<ProgramType[]>([]);

  protected isRecordingType = computed(() => {
    if (!this.selectedProgramTypeId()) return true;
    const type = this.allProgramTypes().find((t) => t.id === this.selectedProgramTypeId());
    return type?.code.toLowerCase() === 'ir' || type?.code.toLowerCase() === 'rec';
  });

  protected programName = signal('');
  protected selectedProgramTypeId = signal('');
  protected selectedSubstantiveAreaId = signal('');
  protected selectedSituationTaxonomyId = signal('');
  protected selectedResponseTaxonomyId = signal('');
  protected loadingOptions = signal(false);
  protected submitting = signal(false);
  protected removing = signal(false);
  protected confirmRemove = signal(false);
  protected showErrors = signal(false);

  protected isValid = computed(
    () =>
      !!this.programName().trim() &&
      !!this.selectedProgramTypeId() &&
      (!this.isRecordingType() || !!this.selectedSubstantiveAreaId()) &&
      !!this.selectedSituationTaxonomyId() &&
      (!this.isRecordingType() || !!this.selectedResponseTaxonomyId()) &&
      (!this.isRecordingType() || this.locations().length > 0) &&
      this.dataElements().every((el) => !!el.beginDate),
  );

  protected programTypeOptions = signal<SelectOption[]>([]);
  protected substantiveAreaOptions = signal<SelectOption[]>([]);
  protected situationTaxonomyOptions = signal<SelectOption[]>([]);
  protected responseTaxonomyOptions = signal<SelectOption[]>([]);

  protected personTypeOptions = signal<SelectOption[]>([]);
  protected locationOptions = signal<SelectOption[]>([]);
  protected locationSearch = signal('');
  protected locationDropdownOpen = signal(false);
  protected newLocationStatus = signal<LocationStatus>('Active');
  protected newStatusDropdownOpen = signal(false);
  protected locations = signal<ProgramLocation[]>([]);

  protected filteredLocationOptions = computed(() => {
    const q = this.locationSearch().toLowerCase().trim();
    const addedIds = new Set(this.locations().map((l) => l.locationId));
    const addedNames = new Set(this.locations().map((l) => l.name.toLowerCase()));
    return this.locationOptions()
      .filter((o) => !addedIds.has(o.value) && !addedNames.has(o.label.toLowerCase()))
      .filter((o) => !q || o.label.toLowerCase().includes(q));
  });

  protected locationNameWarning = computed(() => {
    const name = this.locationSearch().trim().toLowerCase();
    if (!name) return false;
    return this.locations().some((l) => l.name.toLowerCase() === name);
  });

  protected dataElements = signal<DataElementRow[]>([]);
  protected configuredDataElementOptions = signal<SelectOption[]>([]);
  private allConfiguredDataElements = signal<ConfiguredDataElement[]>([]);
  private allSituationTaxonomies = signal<SituationTaxonomy[]>([]);
  private allResponseTaxonomies = signal<ResponseTaxonomy[]>([]);

  protected selectedSituationTaxonomy = computed(
    () =>
      this.allSituationTaxonomies().find((t) => t.id === this.selectedSituationTaxonomyId()) ??
      null,
  );
  protected selectedResponseTaxonomy = computed(
    () =>
      this.allResponseTaxonomies().find((t) => t.id === this.selectedResponseTaxonomyId()) ?? null,
  );

  constructor() {
    effect(() => {
      const p = this.program();
      this.programName.set(p?.name ?? '');
      this.selectedProgramTypeId.set('');
      this.selectedSubstantiveAreaId.set('');
      this.selectedSituationTaxonomyId.set('');
      this.selectedResponseTaxonomyId.set('');
      this.locations.set([]);
    });
  }

  ngOnInit(): void {
    window.addEventListener('scroll', this.repositionAddElement, true);
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadingOptions.set(true);
    const program = this.program();
    forkJoin({
      programTypes: this.programService.getProgramTypes(),
      substantiveAreas: this.programService.getSubstantiveAreas({ pageSize: 100, pageIndex: 0 }),
      situationTaxonomies: this.taxonomyService.getSituationTaxonomies(),
      responseTaxonomies: this.taxonomyService.getResponseTaxonomies(),
      locations: this.programService.getLocations(),
      personTypes: this.programService.getPIInvolvementTypes(),
      configuredDataElements: this.dataElementService.getDataElements(),
      detail: program ? this.programService.getProgramById(program.id) : of(null),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const situationTaxonomies = res.situationTaxonomies.situationTaxonomies ?? [];
          const responseTaxonomies = res.responseTaxonomies.responseTaxonomies ?? [];
          const configuredDataElements = res.configuredDataElements.configuredDataElements ?? [];
          const locations = res.locations.locations ?? [];

          this.allSituationTaxonomies.set(situationTaxonomies);
          this.allResponseTaxonomies.set(responseTaxonomies);
          this.allConfiguredDataElements.set(configuredDataElements);

          this.allProgramTypes.set(res.programTypes.programTypes);
          this.programTypeOptions.set(
            res.programTypes.programTypes.map((t) => ({ value: t.id, label: t.name })),
          );
          this.substantiveAreaOptions.set(
            res.substantiveAreas.substantiveAreas.map((a) => ({ value: a.id, label: a.name })),
          );
          this.situationTaxonomyOptions.set(
            situationTaxonomies.map((t) => ({ value: t.id, label: t.name })),
          );
          this.responseTaxonomyOptions.set(
            responseTaxonomies.map((t) => ({ value: t.id, label: t.taxonomyName })),
          );
          this.locationOptions.set(locations.map((l) => ({ value: l.id, label: l.locationName })));
          this.personTypeOptions.set(
            (res.personTypes ?? []).map((t) => ({ value: t.id, label: t.name })),
          );
          this.configuredDataElementOptions.set(
            configuredDataElements.map((e) => ({ value: e.id, label: e.name })),
          );
          if (res.detail) this.prefillFromDetail(res.detail);
          this.loadingOptions.set(false);
        },
        error: () => this.loadingOptions.set(false),
      });
  }

  private prefillFromDetail(detail: ProgramDetail): void {
    this.selectedProgramTypeId.set(detail.programType?.id ?? '');
    this.selectedSubstantiveAreaId.set(detail.substantiveArea?.id ?? '');
    this.selectedSituationTaxonomyId.set(detail.situationTaxonomy?.id ?? '');
    this.selectedResponseTaxonomyId.set(detail.responseTaxonomy?.id ?? '');
    this.locations.set(
      (detail.programLocations ?? []).map((pl) => {
        const dbLoc = this.locationOptions().find(
          (o) => o.label.toLowerCase() === pl.location.locationName.toLowerCase(),
        );
        return {
          locationId: dbLoc?.value ?? crypto.randomUUID(),
          name: pl.location.locationName,
          status: (pl.status ? 'Active' : 'Inactive') as LocationStatus,
          isCustom: !dbLoc,
        };
      }),
    );
    this.dataElements.set(
      [...(detail.cdePrograms ?? [])]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((cdp) => {
          const elem = this.allConfiguredDataElements().find(
            (e) => e.id === cdp.configuredDataElementId,
          );
          return {
            id: cdp.configuredDataElementId,
            cdeProgramId: cdp.id,
            name: elem?.name ?? cdp.configuredDataElementId,
            beginDate: cdp.beginDate.split('T')[0],
            endDate: cdp.endDate ? cdp.endDate.split('T')[0] : null,
            levelIncidentPI: cdp.levelIncidentPI ?? 0,
            piInvolvementTypeId: cdp.piInvolvementTypeId ?? null,
            requiredOptional: cdp.requiredOptional ?? null,
          };
        }),
    );
  }

  protected addLocation(locationId: string): void {
    if (!locationId) return;
    const already = this.locations().some((l) => l.locationId === locationId);
    if (already) return;
    const label = this.locationOptions().find((o) => o.value === locationId)?.label ?? locationId;
    this.locations.update((list) => [
      ...list,
      { locationId, name: label, status: this.newLocationStatus(), isCustom: false },
    ]);
  }

  protected openStatusLocationId = signal<string | null>(null);
  protected openLevelElementId = signal<string | null>(null);
  protected openPITypeElementId = signal<string | null>(null);

  protected piTypeLabelFor(id: string | null): string {
    if (!id) return 'All';
    return this.personTypeOptions().find((o) => o.value === id)?.label ?? 'All';
  }

  protected openAddElementDropdown = signal(false);
  private addElementTriggerEl: HTMLElement | null = null;

  protected addElementDropdownPos = signal<{
    top?: string;
    bottom?: string;
    left: string;
    width: string;
  } | null>(null);

  protected closeLevelDropdown(): void {
    setTimeout(() => this.openLevelElementId.set(null), 150);
  }

  protected closePITypeDropdown(): void {
    setTimeout(() => this.openPITypeElementId.set(null), 150);
  }

  private readonly ELEMENT_DROPDOWN_H = 240;

  private computeAddElementPosition(): void {
    if (!this.addElementTriggerEl) return;
    const rect = this.addElementTriggerEl.getBoundingClientRect();
    const h = this.ELEMENT_DROPDOWN_H;
    const spaceBelow = window.innerHeight - rect.bottom;
    const up = spaceBelow < h && rect.top > spaceBelow;
    this.addElementDropdownPos.set(
      up
        ? {
            bottom: `${Math.max(8, window.innerHeight - rect.top + 4)}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
          }
        : {
            top: `${Math.min(rect.bottom + 4, window.innerHeight - h - 8)}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
          },
    );
  }

  private repositionAddElement = (): void => {
    if (!this.openAddElementDropdown()) return;
    this.computeAddElementPosition();
  };

  protected toggleAddElementDropdown(event: MouseEvent): void {
    if (!this.openAddElementDropdown()) {
      this.addElementTriggerEl = (event.currentTarget as HTMLElement).parentElement!;
      this.computeAddElementPosition();
    } else {
      this.addElementTriggerEl = null;
    }
    this.openAddElementDropdown.update((v) => !v);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.repositionAddElement, true);
  }

  protected closeAddElementDropdown(): void {
    setTimeout(() => this.openAddElementDropdown.set(false), 150);
  }

  protected selectDataElement(id: string): void {
    if (!id || this.dataElements().some((r) => r.id === id)) return;
    const opt = this.configuredDataElementOptions().find((o) => o.value === id);
    if (!opt) return;
    this.dataElements.update((rows) => [
      ...rows,
      {
        id,
        name: opt.label,
        beginDate: '',
        endDate: '',
        levelIncidentPI: 0,
        piInvolvementTypeId: null,
        requiredOptional: null,
      },
    ]);
    this.openAddElementDropdown.set(false);
  }

  protected setLocationStatus(locationId: string, status: LocationStatus): void {
    this.locations.update((list) =>
      list.map((l) => (l.locationId === locationId ? { ...l, status } : l)),
    );
    this.openStatusLocationId.set(null);
  }

  protected closeLocationStatus(): void {
    setTimeout(() => this.openStatusLocationId.set(null), 150);
  }

  protected removeLocation(locationId: string): void {
    this.locations.update((list) => list.filter((l) => l.locationId !== locationId));
  }

  protected selectLocation(locationId: string): void {
    this.addLocation(locationId);
    this.locationSearch.set('');
    this.locationDropdownOpen.set(false);
  }

  protected closeLocationDropdown(): void {
    setTimeout(() => this.locationDropdownOpen.set(false), 150);
  }

  protected closeStatusDropdown(): void {
    setTimeout(() => this.newStatusDropdownOpen.set(false), 150);
  }

  protected addDataElement(id: string, selectEl: HTMLSelectElement): void {
    if (!id) return;
    if (this.dataElements().some((r) => r.id === id)) {
      selectEl.value = '';
      return;
    }
    const opt = this.configuredDataElementOptions().find((o) => o.value === id);
    if (!opt) return;
    this.dataElements.update((rows) => [
      ...rows,
      {
        id,
        name: opt.label,
        beginDate: '',
        endDate: '',
        levelIncidentPI: 0,
        piInvolvementTypeId: null,
        requiredOptional: null,
      },
    ]);
    selectEl.value = '';
  }

  protected updateDataElementDates(id: string, range: DateRange): void {
    this.dataElements.update((rows) =>
      rows.map((r) => (r.id === id ? { ...r, beginDate: range.from, endDate: range.to } : r)),
    );
  }

  protected updateDataElementLevel(id: string, level: number): void {
    this.dataElements.update((rows) =>
      rows.map((r) =>
        r.id === id
          ? {
              ...r,
              levelIncidentPI: level,
              piInvolvementTypeId: level === 0 ? null : r.piInvolvementTypeId,
            }
          : r,
      ),
    );
  }

  protected updateDataElementPIType(id: string, piInvolvementTypeId: string | null): void {
    this.dataElements.update((rows) =>
      rows.map((r) => (r.id === id ? { ...r, piInvolvementTypeId } : r)),
    );
  }

  protected updateDataElementRequired(id: string, checked: boolean): void {
    this.dataElements.update((rows) =>
      rows.map((r) => (r.id === id ? { ...r, requiredOptional: checked ? 1 : 0 } : r)),
    );
  }

  protected removeDataElement(id: string): void {
    this.dataElements.update((rows) => rows.filter((r) => r.id !== id));
  }

  protected dragIndex = signal<number | null>(null);
  protected dropTarget = signal<number | null>(null);

  protected onDragStart(index: number): void {
    this.dragIndex.set(index);
  }

  protected onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    this.dropTarget.set(index);
  }

  protected onDrop(index: number): void {
    const from = this.dragIndex();
    if (from === null || from === index) {
      this.onDragEnd();
      return;
    }
    this.dataElements.update((rows) => {
      const updated = [...rows];
      const [moved] = updated.splice(from, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    this.onDragEnd();
  }

  protected onDragEnd(): void {
    this.dragIndex.set(null);
    this.dropTarget.set(null);
  }

  protected onRemove(): void {
    const program = this.program();
    if (!program) return;
    if (!this.confirmRemove()) {
      this.confirmRemove.set(true);
      return;
    }
    this.removing.set(true);
    this.programService
      .deleteProgram(program.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Program deleted successfully');
          this.removed.emit();
        },
        error: (err) => {
          this.toastService.show(apiErrorMessage(err, 'Failed to delete program'), 'error');
          this.removing.set(false);
          this.confirmRemove.set(false);
        },
      });
  }

  protected onSubmit(): void {
    const payload: CreateProgramPayload = {
      name: this.programName(),
      programTypeId: this.selectedProgramTypeId(),
      substantiveAreaId: this.selectedSubstantiveAreaId() || null,
      situationTaxonomyId: this.selectedSituationTaxonomyId(),
      responseTaxonomyId: this.isRecordingType() ? this.selectedResponseTaxonomyId() : null,
      programLocations: this.locations().map((l) => ({
        status: l.status === 'Active',
        location: { ...(l.isCustom ? {} : { id: l.locationId }), locationName: l.name },
      })),
      cdePrograms: this.dataElements().map((el, i) => ({
        ...(el.cdeProgramId ? { id: el.cdeProgramId } : {}),
        beginDate: el.beginDate ? `${el.beginDate}T00:00:00.000Z` : new Date().toISOString(),
        endDate: el.endDate ? `${el.endDate}T00:00:00.000Z` : null,
        displayOrder: i + 1,
        configuredDataElementId: el.id,
        levelIncidentPI: el.levelIncidentPI,
        piInvolvementTypeId: el.piInvolvementTypeId,
        requiredOptional: el.requiredOptional ?? null,
      })),
    };

    this.showErrors.set(true);
    if (!this.isValid()) return;

    this.submitting.set(true);
    const program = this.program();
    const request$ = program
      ? this.programService.updateProgram({ id: program.id, ...payload } as UpdateProgramPayload)
      : this.programService.createProgram(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.submitting.set(false);
        this.toastService.show(
          program ? 'Program updated successfully' : 'Program created successfully',
        );
        this.saved.emit();
      },
      error: (err) => {
        this.toastService.show(
          apiErrorMessage(err, program ? 'Failed to update program' : 'Failed to create program'),
          'error',
        );
        this.submitting.set(false);
      },
    });
  }
}
